// Quota Dashboard Component (Quota Frontend Guide Phase 2.3)
// Displays quota status, progress bars, and management actions

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  RefreshCw, 
  RotateCcw, 
  Plus, 
  Minus, 
  Users,
  AlertTriangle,
  CheckCircle2,
  XCircle
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useQuotas } from "@/hooks/useQuotas";
import { QuotaStatus } from "@/types/survey";

interface QuotaDashboardProps {
  surveyId: number;
  quotasEnabled?: boolean;
}

export function QuotaDashboard({ surveyId, quotasEnabled }: QuotaDashboardProps) {
  const { quotas, loading, error, refetch, resetQuota, adjustQuota } = useQuotas(surveyId);
  const [actionDialog, setActionDialog] = useState<{
    type: "reset" | "adjust";
    quota: QuotaStatus;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [adjustment, setAdjustment] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async () => {
    if (!actionDialog || reason.trim().length < 10) return;
    
    setSubmitting(true);
    
    // Convert null to undefined for the optional groupId parameter
    const groupId = actionDialog.quota.groupId ?? undefined;
    const success = actionDialog.type === "reset"
      ? await resetQuota(
          actionDialog.quota.segmentName,
          reason.trim(),
          groupId
        )
      : await adjustQuota(
          actionDialog.quota.segmentName,
          adjustment,
          reason.trim(),
          groupId
        );
    
    setSubmitting(false);
    
    if (success) {
      setActionDialog(null);
      setReason("");
      setAdjustment(0);
    }
  };

  const getProgressPercent = (quota: QuotaStatus): number => {
    if (!quota.quotaMax) return 0;
    return Math.min((quota.currentCount / quota.quotaMax) * 100, 100);
  };

  // Compute isFull from isOpen (backend uses isOpen)
  const getStatusBadge = (quota: QuotaStatus) => {
    const isFull = !quota.isOpen;
    if (isFull) {
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Full</Badge>;
    }
    if (quota.quotaMax && quota.currentCount >= quota.quotaMax * 0.8) {
      return <Badge className="gap-1 bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500"><AlertTriangle className="h-3 w-3" /> Near Full</Badge>;
    }
    return <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Open</Badge>;
  };

  if (!quotasEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Quota Management
          </CardTitle>
          <CardDescription>
            Quotas are disabled for this survey. Enable them in survey settings to track response limits.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (loading && quotas.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Quota Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground" role="status" aria-live="polite">
            <RefreshCw className="h-4 w-4 animate-spin mr-2" aria-hidden="true" />
            Loading quotas...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Quota Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-destructive" role="alert">
            <AlertTriangle className="h-8 w-8 mb-2" aria-hidden="true" />
            <p>{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={refetch} aria-label="Retry loading quotas">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quotas.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Quota Management
          </CardTitle>
          <CardDescription>
            No quotas configured. Add segmentation rules with quota limits to track response counts.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">
                {quotas.length} quota{quotas.length !== 1 ? 's' : ''} configured
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={refetch} disabled={loading} aria-label="Refresh quota data">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {quotas.map((quota) => {
              const percent = getProgressPercent(quota);
              return (
                <div 
                  key={`${quota.segmentName}-${quota.groupId || "default"}`}
                  className="p-4 border border-border rounded-lg"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-foreground">{quota.segmentName}</p>
                      {quota.groupId && (
                        <p className="text-sm text-muted-foreground">Group: {quota.groupId}</p>
                      )}
                    </div>
                    {getStatusBadge(quota)}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {quota.currentCount} / {quota.quotaMax ?? '∞'}
                      </span>
                      <span className="text-muted-foreground">{Math.round(percent)}%</span>
                    </div>
                    <Progress 
                      value={percent} 
                      className={`h-2 ${percent >= 100 ? '[&>div]:bg-destructive' : percent >= 80 ? '[&>div]:bg-amber-500' : ''}`}
                    />
                  </div>
                  
                  {/* Show remaining slots if quota has a max */}
                  {quota.remainingSlots !== null && quota.remainingSlots > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {quota.remainingSlots} slot{quota.remainingSlots !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                  
                  {/* Show needed count if below minimum */}
                  {quota.quotaMin > 0 && quota.currentCount < quota.quotaMin && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                      Need {quota.quotaMin - quota.currentCount} more to meet minimum quota
                    </p>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setActionDialog({ type: "adjust", quota })}
                      aria-label={`Adjust quota for ${quota.segmentName}`}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Adjust
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setActionDialog({ type: "reset", quota })}
                      aria-label={`Reset quota for ${quota.segmentName}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Reset
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === "reset" ? "Reset Quota" : "Adjust Quota"}
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.type === "reset" 
                ? `Reset the count for "${actionDialog?.quota.segmentName}" to 0. This action is logged.`
                : `Adjust the count for "${actionDialog?.quota.segmentName}". Use negative numbers to decrease.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {actionDialog?.type === "adjust" && (
              <div className="space-y-2">
                <Label htmlFor="adjustment">Adjustment</Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setAdjustment(a => a - 1)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    id="adjustment"
                    type="number"
                    value={adjustment}
                    onChange={(e) => setAdjustment(parseInt(e.target.value) || 0)}
                    className="text-center w-24"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setAdjustment(a => a + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {actionDialog.quota.currentCount !== undefined && (
                  <p className="text-xs text-muted-foreground">
                    New count will be: {Math.max(0, actionDialog.quota.currentCount + adjustment)}
                  </p>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason <span className="text-muted-foreground">(required, 10-500 chars)</span>
              </Label>
              <Textarea
                id="reason"
                placeholder="Explain why you're making this adjustment..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">
                {reason.length}/500 characters (min: 10)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={submitting || reason.trim().length < 10}
              variant={actionDialog?.type === "reset" ? "destructive" : "default"}
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                actionDialog?.type === "reset" ? "Reset to 0" : "Apply Adjustment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
